import type { Folder, NoteSummary } from '@kanto/shared';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { formatRemindAt } from '../lib/dates';
import { useT } from '../lib/i18n';
import { TagChips } from './TagChips';

/**
 * One note in the list: its title, one line of what it says, its tags, and when
 * it was last touched.
 *
 * **The whole row is a link**, unlike a todo, which is edited in place. A note is
 * a document — there is nothing useful to do to it from a list, and a row that
 * opened only from a small target would be a list that is annoying on a phone.
 *
 * The two actions sit over the right of the row on hover and on `focus-within`,
 * as they do on a todo, and they are the two a list can honestly offer: file it
 * somewhere, or take it off the list. Editing happens on the note's own screen.
 *
 * The moment is `updatedAt` and not `createdAt`: the list is ordered by it, so
 * showing anything else would put a date on screen that does not explain the
 * order it is in.
 */
export function NoteRow({
  note,
  folders,
  onMove,
  onArchive,
}: {
  note: NoteSummary;
  folders: Folder[];
  onMove: (folderId: string | null) => void;
  onArchive: () => void;
}): ReactElement {
  const t = useT();

  return (
    <li className="group relative">
      <Link
        to={`/notes/${note.id}`}
        className="flex items-start gap-3 bg-surface px-4 py-3 transition-colors hover:bg-paper"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-medium text-ink-900">
            {note.title === '' ? t('notes.untitled') : note.title}
          </p>
          {note.excerpt !== '' && (
            <p className="mt-0.5 truncate text-sm text-ink-500">{note.excerpt}</p>
          )}
          <TagChips tags={note.tags} />
        </div>

        <span className="mt-0.5 shrink-0 text-sm tabular-nums text-ink-400">
          {formatRemindAt(note.updatedAt, t)}
        </span>
      </Link>

      {/* Laid over the right of the row rather than beside it: reserved inline,
          their width would be a hand's breadth of empty space in every row.
          Outside the link, because a `<select>` inside one is a control that
          navigates when it is opened. */}
      <div className="absolute inset-y-0 right-0 hidden items-center gap-1 bg-surface pr-3 pl-6 group-focus-within:flex group-hover:flex">
        <label className="sr-only" htmlFor={`folder-${note.id}`}>
          {t('notes.folder')}
        </label>
        <select
          id={`folder-${note.id}`}
          value={note.folderId ?? ''}
          onChange={(event) => onMove(event.target.value === '' ? null : event.target.value)}
          className="min-h-9 max-w-36 rounded-lg border border-line bg-surface px-2 text-xs text-ink-500"
        >
          <option value="">{t('notes.inbox')}</option>
          {folders.map((folder) => (
            <option key={folder.id} value={folder.id}>
              {folder.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={onArchive}
          aria-label={t('notes.archive', note.title === '' ? t('notes.untitled') : note.title)}
          className="flex min-h-9 min-w-9 items-center justify-center rounded-lg text-ink-400 transition-colors hover:bg-alert-soft hover:text-alert"
        >
          <svg
            viewBox="0 0 24 24"
            className="size-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 7h18v3H3zM5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9M10 14h4" />
          </svg>
        </button>
      </div>
    </li>
  );
}
